import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    CircularProgress, Button, Box, Typography, Alert,
} from "@mui/material";
import NavBar from "../navbar";
import { validateLoggedIn, getSession } from "../utils";
import { useNavigate, Link } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const EndpointRestrictions = () => {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) { setError(validate); return; }

        const fetchData = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + "/admin/endpoint-restrictions", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
                setData(await response.json());
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            }
        };
        fetchData();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };


    const sortData = (rows, orderBy, order) => {
        return [...rows].sort((a, b) => {
            const valA = a[orderBy]; const valB = b[orderBy];
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            if (typeof valA === "number" && typeof valB === "number") return order === "asc" ? valA - valB : valB - valA;
            if (typeof valA === "string" && typeof valB === "string") return order === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
            return order === "asc" ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
        });
    };

    if (error) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;
    if (!data) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;

    const sortedRows = data.length > 0 ? sortData(data, orderBy, order) : [];

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" component="h1">Endpoint Restrictions</Typography>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate("/endpoint-restrictions/new")}>
                        Create Restriction
                    </Button>
                </Box>
                {data.length === 0 ? (
                    <Alert severity="info">No endpoint restrictions found.</Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell column="endpoint_path" label="Endpoint" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="plan_name" label="Plan" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="price" label="Price" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="increase_every" label="Increase Every" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="max_results" label="Max Results" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <TableCell>Disabled</TableCell>
                                    <SortableTableCell column="created_at" label="Created At" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedRows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><Link to={`/endpoints/${row.endpoint_id}/edit`}>{row.endpoint_path}</Link></TableCell>
                                        <TableCell><Link to={`/plans/${row.plan_id}/edit`}>{row.plan_name}</Link></TableCell>
                                        <TableCell>{row.price}</TableCell>
                                        <TableCell>{row.increase_every !== null ? row.increase_every : "—"}</TableCell>
                                        <TableCell>{row.max_results !== null ? row.max_results : "—"}</TableCell>
                                        <TableCell>{row.disabled ? "Yes" : "No"}</TableCell>
                                        <TableCell>{formatUtc(row.created_at)}</TableCell>
                                        <TableCell align="center">
                                            <Button variant="outlined" size="small" onClick={() => navigate(`/endpoint-restrictions/${row.id}/edit`)}>
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    );
};

const SortableTableCell = ({ column, label, orderBy, order, handleSort }) => (
    <TableCell onClick={() => handleSort(column)} style={{ cursor: "pointer" }}>
        {label} {orderBy === column && (order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
    </TableCell>
);

export default EndpointRestrictions;
